import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon, ArrowTopRightOnSquareIcon, QrCodeIcon, ShieldCheckIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { fetchFunction } from "../../api/fetchFunction";
import { readSSEStream } from "../../utils/readSSEStream";
import EventForm from "../EventForm";
import QRCodeComponent from "../../components/QRComponent";
import { BUTTON_CLASSES } from "./constants";

// Event Preview Panel Component
export const EventPreviewPanel = ({ event, onClose }) => {
  const [showQR, setShowQR] = useState(false);

  if (!event) return null;

  return (
    <Dialog open={true} onClose={() => { if (showQR) setShowQR(false); else onClose(); }} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/50 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-300 ease-in-out data-[closed]:translate-x-full"
            >
              <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate pr-4">{event.name}</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowQR(true)}
                      className="rounded-full p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Show QR Code"
                    >
                      <QrCodeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => window.open(`/events/${event.id}/edit`, '_blank')}
                      className="rounded-full p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Open in new tab"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={onClose}
                      className="rounded-full p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Event Form Content */}
                <div className="flex-1 overflow-y-auto p-4 bg-stone-100 dark:bg-gray-900">
                  <EventForm eventID={event.id} embedded />
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
      {showQR && (
        <QRCodeComponent
          hideQR={() => setShowQR(false)}
          currentEvent={event}
        />
      )}
    </Dialog>
  );
};

const SEVERITY_STYLES = {
  high: { badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", icon: ExclamationTriangleIcon, iconColor: "text-red-500 dark:text-red-400" },
  medium: { badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200", icon: ExclamationTriangleIcon, iconColor: "text-yellow-500 dark:text-yellow-400" },
  low: { badge: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300", icon: InformationCircleIcon, iconColor: "text-gray-400 dark:text-gray-500" },
};

export const HealthCheckModal = ({ open, onClose }) => {
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [steps, setSteps] = useState([]);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    setError(null);
    setSteps([]);
    try {
      const response = await fetchFunction("eventHealthAgentStream", { dryRun });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      await readSSEStream(response, (event) => {
        if (event.type === "step") setSteps(prev => [...prev, event.message]);
        else if (event.type === "done") setResults(event.data);
        else if (event.type === "error") setError(event.message);
      });
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setResults(null);
    setError(null);
    onClose();
  };

  const issueCount = results?.results?.reduce((acc, r) => acc + (r.newIssues?.length || 0), 0) ?? 0;

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Event Health Check</h2>
            </div>
            <button onClick={handleClose} className="rounded-full p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Controls */}
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-300">Dry run</span>
              <button
                onClick={() => setDryRun(v => !v)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${dryRun ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${dryRun ? "translate-x-5" : "translate-x-0"}`} />
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500">{dryRun ? "No SMS will be sent" : "Will send real SMS messages"}</span>
            </div>
            <button
              onClick={runCheck}
              disabled={loading}
              className={`${BUTTON_CLASSES.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Checking...
                </>
              ) : "Run Check"}
            </button>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {!results && !error && !loading && (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
                Checks upcoming events in the next 7 days for weather issues, competing events, and venue conflicts.
              </p>
            )}

            {loading && (
              <div className="py-8 px-2 space-y-4">
                {steps.length === 0 && (
                  <div className="flex items-center gap-3 text-sm text-gray-400 dark:text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent flex-shrink-0" />
                    <span>Connecting…</span>
                  </div>
                )}
                {steps.map((msg, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    {i < steps.length - 1 ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                    ) : (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent flex-shrink-0" />
                    )}
                    <span className={i < steps.length - 1 ? "text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-gray-100 font-medium"}>{msg}</span>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>
            )}

            {results && (
              <>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{results.results.length} event{results.results.length !== 1 ? "s" : ""} checked</span>
                  <span>·</span>
                  {issueCount > 0 ? (
                    <span className="text-red-600 dark:text-red-400 font-medium">{issueCount} new issue{issueCount !== 1 ? "s" : ""} found</span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400 font-medium">No new issues</span>
                  )}
                  {results.dryRun && <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200 px-2 py-0.5 rounded-full">dry run</span>}
                </div>

                {results.results.map((r, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.name || r.eventId}</span>
                      {r.skipped ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">skipped — {r.reason}</span>
                      ) : r.error ? (
                        <span className="text-xs text-red-500 dark:text-red-400">error</span>
                      ) : r.newIssues?.length > 0 ? (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">{r.newIssues.length} new issue{r.newIssues.length !== 1 ? "s" : ""}</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="h-3.5 w-3.5" /> All clear
                        </span>
                      )}
                    </div>

                    {r.newIssues?.length > 0 && (
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {r.newIssues.map((issue, j) => {
                          const style = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.low;
                          const Icon = style.icon;
                          return (
                            <div key={j} className="px-4 py-3 space-y-1">
                              <div className="flex items-start gap-2">
                                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{issue.title}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${style.badge}`}>{issue.severity}</span>
                                    {issue.canReschedule && (
                                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium">can reschedule</span>
                                    )}
                                    {issue.suggestCancel && (
                                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 font-medium">consider cancelling</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{issue.description}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">💡 {issue.suggestion}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {r.error && (
                      <div className="px-4 py-3 text-xs text-red-600 dark:text-red-400">{r.error}</div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
